import { setUrlPathParams } from '@lib/utils/setUrlPathParams';
import { setUrlQueryParams } from '@lib/utils/setUrlQueryParams';

import type { AppRouteEnum } from '@lib/enums/AppRouteEnum';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

export class UrlBuilder {
  constructor(
    private router: Maybe<AppRouterInstance>,
    private route: AppRouteEnum,
  ) {}

  /**
   * Set the value of :id on a path segment
   *
   * For routes with multiple :id parameters this method can be chained
   *
   * ```ts
   * const router = useAppRouter();
   * router.build(AppRouteEnum.ConsumerProfile).setId('123').setId('1234').getAppRoute();
   * ```
   */
  public setId(id: Maybe<string | number>): this {
    this.route = setUrlPathParams(this.route, id ?? 'null') as AppRouteEnum;
    return this;
  }

  public setQuery(params: Maybe<Record<string, unknown>>): this {
    if (params == null) {
      return this;
    }

    this.route = setUrlQueryParams(this.route, params) as AppRouteEnum;
    return this;
  }

  /**
   * Return the string result after building route
   */
  public getAppRoute(): AppRouteEnum {
    return this.route;
  }

  /**
   * Navigate to route after building. No-ops when built without a router (string-only use).
   */
  public go(config: { replace: boolean } = { replace: false }): void {
    if (this.router == null) {
      return;
    }

    if (config.replace) {
      this.router.replace(this.route);
      return;
    }

    this.router.push(this.route);
  }
}
